import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, DrawingManager } from '@react-google-maps/api';
import { Card, Form, Button, Row, Col, Alert, Badge, Table, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import DxfParser from 'dxf-parser';
import API_URL, { GOOGLE_MAPS_API_KEY } from '../../config';
import ImovelSelector from '../Imovel/ImovelSelector';

const LIBRARIES = ['drawing', 'geometry'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '450px' };
const DEFAULT_CENTER = { lat: -23.5505, lng: -46.6333 };

const FATORES_LOTE_DEFAULT = {
    ftopo: [
        { id: 'plano',        descricao: 'Plano (0–3%) — padrão',           fator: 1.00 },
        { id: 'aclive_suave', descricao: 'Aclive suave (3–6%)',              fator: 0.95 },
        { id: 'declive_suave',descricao: 'Declive suave (3–6%)',             fator: 0.90 },
        { id: 'aclive_mod',   descricao: 'Aclive moderado (6–12%)',          fator: 0.95 },
        { id: 'declive_mod',  descricao: 'Declive moderado (6–12%)',         fator: 0.85 },
        { id: 'aclive_acent', descricao: 'Aclive acentuado (12–30%)',        fator: 0.90 },
        { id: 'declive_acent',descricao: 'Declive acentuado (12–30%)',       fator: 0.75 },
        { id: 'abaixo_nivel', descricao: 'Abaixo do nível da rua',           fator: 0.70 },
    ],
    fforma: [
        { id: 'regular',  descricao: 'Regular padrão — retangular/proporcional', fator: 1.00 },
        { id: 'irr_mod',  descricao: 'Irregular moderada — triangular/trapezoidal', fator: 0.85 },
        { id: 'irr_sev',  descricao: 'Irregular severa — ângulos agudos/assimétrico', fator: 0.70 },
    ],
    finfra: [
        { id: 'completa',       descricao: 'Completa — pavim., água, esgoto, energia, ilum., coleta', fator: 1.00 },
        { id: 'quase_completa', descricao: 'Quase completa — falta 1 elemento (ex: drenagem)',         fator: 0.95 },
        { id: 'parcial',        descricao: 'Parcial — falta 2 a 3 serviços (ex: sem pavim./drenagem)', fator: 0.90 },
        { id: 'precaria',       descricao: 'Precária — sem pavim. e maioria das redes ausentes',       fator: 0.85 },
        { id: 'inexistente',    descricao: 'Inexistente — sem qualquer serviço, acesso rústico',        fator: 0.80 },
    ],
    ffre: [
        { id: 'uma',       descricao: 'Uma frente — padrão',        fator: 1.00 },
        { id: 'duas',      descricao: 'Duas frentes',               fator: 1.10 },
        { id: 'esquina',   descricao: 'Esquina',                    fator: 1.15 },
        { id: 'tres_mais', descricao: 'Três ou mais frentes',       fator: 1.20 },
    ],
    farea: [
        { id: 'mp',  descricao: 'Muito pequeno (≤ 0,50 Ap)',                limite_sup: 0.50,  fator: 1.10 },
        { id: 'p',   descricao: 'Pequeno (0,51–0,80 Ap)',                   limite_sup: 0.80,  fator: 1.05 },
        { id: 'pad', descricao: 'Padrão (0,81–1,20 Ap)',                    limite_sup: 1.20,  fator: 1.00 },
        { id: 'il',  descricao: 'Lote Ampliado (1,21–2,00 Ap)',             limite_sup: 2.00,  fator: 0.95 },
        { id: 'lpd', descricao: 'Lote Para Desenvolvimento (2,01–4,00 Ap)', limite_sup: 4.00,  fator: 0.90 },
        { id: 'gup', descricao: 'GLEBA Urbana Pequena (4,01–10,00 Ap)',     limite_sup: 10.00, fator: 0.60 },
        { id: 'gu',  descricao: 'GLEBA Urbana (> 10,00 Ap)',                limite_sup: 9999,  formula: 'power_neg04' },
    ],
    fprof: [
        { id: 'mp',  descricao: 'Muito profundo (FP < 0,25)',     limite_sup: 0.25,  fator: 0.85 },
        { id: 'p',   descricao: 'Profundo (0,25 ≤ FP < 0,40)',    limite_sup: 0.40,  fator: 0.90 },
        { id: 'pad', descricao: 'Equilibrado (0,40 ≤ FP ≤ 0,70)', limite_sup: 0.70,  fator: 1.00 },
        { id: 'l',   descricao: 'Largo (0,70 < FP ≤ 1,00)',       limite_sup: 1.00,  fator: 1.05 },
        { id: 'ml',  descricao: 'Muito largo (FP > 1,00)',         limite_sup: 9999,  fator: 1.10 },
    ],
    encravado_fator: 0.75,
};

// Cache DXF persistente entre navegações (módulo-level — sobrevive unmount do componente)
const _dxfCache = { polygons: [], fileName: '', unit: 'm', selectedId: null, selectedEdges: [] };

// Shoelace formula — área de polígono plano em unidades do desenho
function shoelaceArea(points) {
    let sum = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        sum += points[i].x * points[j].y;
        sum -= points[j].x * points[i].y;
    }
    return Math.abs(sum) / 2;
}

// Ray-casting: ponto (px,py) está dentro do polígono pts?
function pointInPolygon(px, py, pts) {
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const xi = pts[i].x, yi = pts[i].y;
        const xj = pts[j].x, yj = pts[j].y;
        if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi))
            inside = !inside;
    }
    return inside;
}

// Distância mínima de um ponto a um segmento
function pointToSegmentDist(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(px - ax, py - ay);
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

// Ponto mais próximo sobre a fronteira do polígono (snap exato na aresta)
function closestPointOnPolygon(px, py, pts) {
    let best = { x: pts[0].x, y: pts[0].y }, bestDist = Infinity;
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const ax = pts[i].x, ay = pts[i].y, bx = pts[j].x, by = pts[j].y;
        const dx = bx - ax, dy = by - ay;
        const lenSq = dx * dx + dy * dy;
        const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
        const cx = ax + t * dx, cy = ay + t * dy;
        const d = Math.hypot(px - cx, py - cy);
        if (d < bestDist) { bestDist = d; best = { x: cx, y: cy }; }
    }
    return best;
}

// Extrai polilíneas fechadas de um DXF parseado
// e associa o código da layer LEV_CODIGO usando point-in-polygon
function extractPolygons(dxf) {
    const polygons = [];
    const labels = []; // { x, y, text }
    const valoresGenerico = []; // { x, y, text }
    const valoresFaceQuadra = []; // { x, y, text, value }
    const entities = dxf?.entities || [];

    entities.forEach((e, idx) => {
        // Polígonos
        if (e.type === 'LWPOLYLINE' || e.type === 'POLYLINE') {
            if (e.shape || e.closed) {
                const pts = e.vertices.map(v => ({ x: v.x, y: v.y }));
                if (pts.length >= 3) {
                    const area = shoelaceArea(pts);
                    if (area > 0) polygons.push({ id: idx, pts, area, layer: e.layer || '0', codigo: null, valorGenerico: null });
                }
            }
        }
        // Textos — layers de código e de valor genérico
        if (e.type === 'TEXT' || e.type === 'MTEXT') {
            const layerUp = (e.layer || '').toUpperCase();
            const x = e.startPoint?.x ?? e.position?.x ?? 0;
            const y = e.startPoint?.y ?? e.position?.y ?? 0;
            const text = (e.text || e.string || '').trim();
            if (!text) { /* skip */ }
            else if (layerUp.includes('CODIGO') || layerUp.includes('CÓDIGO')) {
                labels.push({ x, y, text });
            } else if (layerUp.includes('FACE_QUADRA')) {
                const num = parseFloat(text.replace(',', '.'));
                if (!isNaN(num)) valoresFaceQuadra.push({ x, y, text, value: num, rotation: e.rotation || 0 });
            } else if (layerUp.includes('VALOR_GENERICO') || (layerUp.includes('VALOR') && layerUp.includes('GENERICO'))) {
                valoresGenerico.push({ x, y, text });
            }
        }
    });

    if (labels.length > 0) {
        // 1ª passagem: point-in-polygon (mais preciso)
        const usedLabels = new Set();
        labels.forEach(lbl => {
            for (const poly of polygons) {
                if (poly.codigo === null && pointInPolygon(lbl.x, lbl.y, poly.pts)) {
                    poly.codigo = lbl.text;
                    usedLabels.add(lbl);
                    break;
                }
            }
        });
        // Polígonos sem label correspondente ficam com codigo = null (exibido em branco)
    }

    // Associa VALOR_GENERICO_NO_LOTE a cada polígono via point-in-polygon
    if (valoresGenerico.length > 0) {
        valoresGenerico.forEach(vg => {
            for (const poly of polygons) {
                if (poly.valorGenerico === null && pointInPolygon(vg.x, vg.y, poly.pts)) {
                    const num = parseFloat(vg.text.replace(',', '.'));
                    if (!isNaN(num)) poly.valorGenerico = num;
                    break;
                }
            }
        });
        // fallback: centroide mais próximo
        polygons.forEach(poly => {
            if (poly.valorGenerico !== null) return;
            const cx = poly.pts.reduce((s, p) => s + p.x, 0) / poly.pts.length;
            const cy = poly.pts.reduce((s, p) => s + p.y, 0) / poly.pts.length;
            let best = null, bestDist = Infinity;
            valoresGenerico.forEach(vg => {
                const d = Math.hypot(vg.x - cx, vg.y - cy);
                if (d < bestDist) { bestDist = d; best = vg; }
            });
            if (best && bestDist < 50) {
                const num = parseFloat(best.text.replace(',', '.'));
                if (!isNaN(num)) poly.valorGenerico = num;
            }
        });
    }

    // Associa FACE_QUADRA: cada texto vai para o polígono cuja aresta é a mais próxima (sem duplicar)
    // O snap ancora o badge exatamente na aresta, evitando desvio com zoom alto
    polygons.forEach(poly => { poly.faceQuadraTexts = []; });
    if (valoresFaceQuadra.length > 0) {
        valoresFaceQuadra.forEach(fq => {
            let bestPoly = null, bestDist = Infinity;
            polygons.forEach(poly => {
                for (let i = 0; i < poly.pts.length; i++) {
                    const j = (i + 1) % poly.pts.length;
                    const d = pointToSegmentDist(fq.x, fq.y, poly.pts[i].x, poly.pts[i].y, poly.pts[j].x, poly.pts[j].y);
                    if (d < bestDist) { bestDist = d; bestPoly = poly; }
                }
            });
            if (bestPoly) {
                const snap = closestPointOnPolygon(fq.x, fq.y, bestPoly.pts);
                bestPoly.faceQuadraTexts.push({ ...fq, dist: bestDist, snapX: snap.x, snapY: snap.y });
            }
        });
    }

    return polygons;
}

// Normaliza pontos para viewport SVG
function normalizeToSvg(polygons, svgW, svgH, padding = 20) {
    const allX = polygons.flatMap(p => p.pts.map(pt => pt.x));
    const allY = polygons.flatMap(p => p.pts.map(pt => pt.y));
    const minX = Math.min(...allX), maxX = Math.max(...allX);
    const minY = Math.min(...allY), maxY = Math.max(...allY);
    const rangeX = maxX - minX || 1, rangeY = maxY - minY || 1;
    const scaleX = (svgW - padding * 2) / rangeX;
    const scaleY = (svgH - padding * 2) / rangeY;
    const scale = Math.min(scaleX, scaleY);
    const toSvg = (x, y) => ({
        x: padding + (x - minX) * scale,
        y: svgH - padding - (y - minY) * scale,
    });
    return polygons.map(p => ({
        ...p,
        svgPts: p.pts.map(pt => toSvg(pt.x, pt.y)),
        faceQuadraTexts: (p.faceQuadraTexts || []).map(t => ({
            ...t,
            svgX: toSvg(t.snapX ?? t.x, t.snapY ?? t.y).x,
            svgY: toSvg(t.snapX ?? t.x, t.snapY ?? t.y).y,
        })),
    }));
}

// Centroide de um polígono (média dos vértices)
function centroid(pts) {
    const n = pts.length;
    return { x: pts.reduce((s, p) => s + p.x, 0) / n, y: pts.reduce((s, p) => s + p.y, 0) / n };
}

// ── DXF Viewer com zoom/pan ──────────────────────────────────────────────────
function DxfViewer({ polygons, selectedId, onSelect, unitMultiplier, dxfUnit, selectedEdges, onSelectEdge, focusId, onFaceQuadraSelect }) {
    const SVG_H = 460;
    const [zoom, setZoom] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const dragRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const [svgW, setSvgW] = React.useState(800);

    // useLayoutEffect garante que svgW é lido antes do primeiro paint
    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const w = el.clientWidth;
        if (w > 0) setSvgW(w);
        const ro = new ResizeObserver(entries => {
            const rw = entries[0].contentRect.width;
            if (rw > 0) setSvgW(rw);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Listener de wheel não-passivo (evita scroll da página)
    const zoomRef = React.useRef(zoom);
    const panRef = React.useRef(pan);
    zoomRef.current = zoom;
    panRef.current = pan;

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            // Converte coordenadas de pixel para espaço SVG (considerando viewBox)
            const scaleRatio = svgW / rect.width;
            const cx = (e.clientX - rect.left) * scaleRatio;
            const cy = (e.clientY - rect.top) * scaleRatio;
            const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
            const z = zoomRef.current;
            const nz = Math.min(5000, Math.max(0.2, z * factor));
            const p = panRef.current;
            setZoom(nz);
            setPan({ x: cx - (cx - p.x) * (nz / z), y: cy - (cy - p.y) * (nz / z) });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [svgW]); // re-bind quando svgW muda

    React.useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [polygons]);

    // Zoom para o lote pesquisado
    React.useEffect(() => {
        if (focusId === null || !polygons.length) return;
        const norm = normalizeToSvg(polygons, svgW, SVG_H);
        const poly = norm.find(p => p.id === focusId);
        if (!poly) return;
        const xs = poly.svgPts.map(pt => pt.x);
        const ys = poly.svgPts.map(pt => pt.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const polyW = maxX - minX || 1, polyH = maxY - minY || 1;
        const targetZoom = Math.min((svgW * 0.7) / polyW, (SVG_H * 0.7) / polyH, 5000);
        const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
        setZoom(targetZoom);
        setPan({ x: svgW / 2 - cx * targetZoom, y: SVG_H / 2 - cy * targetZoom });
    }, [focusId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!polygons.length) return null;
    const normalized = normalizeToSvg(polygons, svgW, SVG_H);

    const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y, moved: false };
    };
    const handleMouseMove = (e) => {
        if (!dragRef.current) return;
        dragRef.current.moved = true;
        setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
    };
    const handleMouseUp = () => { dragRef.current = null; };

    const handleDblClick = (e) => {
        const rect = containerRef.current?.getBoundingClientRect();
        const scaleRatio = rect ? svgW / rect.width : 1;
        const cx = rect ? (e.clientX - rect.left) * scaleRatio : svgW / 2;
        const cy = rect ? (e.clientY - rect.top) * scaleRatio : SVG_H / 2;
        const factor = e.shiftKey ? 1 / 2 : 2;
        setZoom(z => {
            const nz = Math.min(5000, Math.max(0.2, z * factor));
            setPan(p => ({ x: cx - (cx - p.x) * (nz / z), y: cy - (cy - p.y) * (nz / z) }));
            return nz;
        });
    };

    const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

    const isDragging = !!dragRef.current;

    return (
        <div style={{ border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa', userSelect: 'none' }}>
            {/* Toolbar */}
            <div className="d-flex align-items-center gap-2 px-2 py-1 border-bottom" style={{ fontSize: 12 }}>
                <button onClick={() => setZoom(z => Math.min(5000, z * 1.3))}
                    style={{ border: '1px solid #ccc', borderRadius: 4, padding: '1px 8px', cursor: 'pointer', background: '#fff', fontSize: 16 }}>+</button>
                <button onClick={() => setZoom(z => Math.max(0.2, z / 1.3))}
                    style={{ border: '1px solid #ccc', borderRadius: 4, padding: '1px 8px', cursor: 'pointer', background: '#fff', fontSize: 16 }}>−</button>
                <span style={{ color: '#666' }}>{Math.round(zoom * 100)}%</span>
                <button onClick={resetView}
                    style={{ border: '1px solid #ccc', borderRadius: 4, padding: '1px 8px', cursor: 'pointer', background: '#fff', fontSize: 12 }}>Reset</button>
                <span style={{ color: '#aaa', marginLeft: 4 }}>Scroll · Arrastar · 2×clique para zoom</span>
                {selectedId !== null && (() => {
                    const p = polygons.find(x => x.id === selectedId);
                    return p ? (
                        <span style={{ marginLeft: 'auto', color: '#0d6efd', fontWeight: 600 }}>
                            Selecionado: {p.codigo ?? `Lote ${p.id + 1}`} — {(p.area * unitMultiplier[dxfUnit]).toFixed(2)} m²
                        </span>
                    ) : null;
                })()}
            </div>
            {/* SVG com viewBox garante que os polígonos sempre preenchem o espaço */}
            <div ref={containerRef}
                style={{ overflow: 'hidden', width: '100%', height: SVG_H, cursor: isDragging ? 'grabbing' : 'grab' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onDoubleClick={handleDblClick}
            >
                <svg width="100%" height={SVG_H} viewBox={`0 0 ${svgW} ${SVG_H}`}
                    preserveAspectRatio="none" style={{ display: 'block' }}>
                    <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                        {normalized.map(p => {
                            const c = centroid(p.svgPts);
                            const isSelected = selectedId === p.id;
                            const xs = p.svgPts.map(pt => pt.x);
                            const ys = p.svgPts.map(pt => pt.y);
                            const bboxW = Math.max(...xs) - Math.min(...xs);
                            const bboxH = Math.max(...ys) - Math.min(...ys);
                            // Adapta ao polígono, com mínimo de 6px e máximo de 11px em tela
                            const fitSize = Math.min(bboxW, bboxH) * 0.28;
                            const fontSize = Math.min(11 / zoom, Math.max(fitSize, 6 / zoom));
                            const origPoly = polygons.find(x => x.id === p.id);
                            return (
                                <g key={p.id}>
                                    {/* Polígono clicável para seleção */}
                                    <polygon
                                        points={p.svgPts.map(pt => `${pt.x},${pt.y}`).join(' ')}
                                        fill={isSelected ? '#1976D2' : '#90CAF9'}
                                        fillOpacity={isSelected ? 0.35 : 0.18}
                                        stroke={isSelected ? '#0D47A1' : '#1976D2'}
                                        strokeWidth={isSelected ? 2 / zoom : 1 / zoom}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => { e.stopPropagation(); if (!dragRef.current?.moved) onSelect(p); }}
                                    />
                                    {/* Arestas clicáveis (testada) — apenas quando lote selecionado */}
                                    {isSelected && p.svgPts.map((pt, i) => {
                                        const j = (i + 1) % p.svgPts.length;
                                        const next = p.svgPts[j];
                                        const isEdgeSel = selectedEdges?.some(e => e.polyId === p.id && e.edgeIdx === i);
                                        const rawLen = origPoly
                                            ? Math.hypot(origPoly.pts[j].x - origPoly.pts[i].x, origPoly.pts[j].y - origPoly.pts[i].y)
                                            : 0;
                                        return (
                                            <g key={`e${i}`} style={{ cursor: 'crosshair' }}
                                                onClick={(e) => { e.stopPropagation(); if (!dragRef.current?.moved) onSelectEdge(p.id, i, rawLen); }}>
                                                <line x1={pt.x} y1={pt.y} x2={next.x} y2={next.y}
                                                    stroke="transparent" strokeWidth={10 / zoom} />
                                                {isEdgeSel && (
                                                    <line x1={pt.x} y1={pt.y} x2={next.x} y2={next.y}
                                                        stroke="#FF6600" strokeWidth={3 / zoom}
                                                        strokeLinecap="round" />
                                                )}
                                            </g>
                                        );
                                    })}
                                    {/* Código/número do lote */}
                                    <text
                                        x={c.x} y={c.y}
                                        textAnchor="middle" dominantBaseline="middle"
                                        fontSize={fontSize}
                                        fill={isSelected ? '#fff' : '#1565C0'}
                                        fontWeight={isSelected ? 700 : 500}
                                        pointerEvents="none"
                                        style={{ paintOrder: 'stroke fill' }}
                                        stroke={isSelected ? '#0D47A1' : '#e8f0fe'}
                                        strokeWidth={fontSize * 0.2}
                                    >
                                        {p.codigo ?? ''}
                                    </text>
                                </g>
                            );
                        })}
                        {/* Badges FACE_QUADRA — camada superior, renderizada após todos os polígonos */}
                        {normalized.flatMap(p =>
                            (p.faceQuadraTexts || []).map((fq, fi) => {
                                const badgeFs = 8 / zoom;
                                const padX = badgeFs * 0.6;
                                const padY = badgeFs * 0.35;
                                const approxW = fq.text.length * badgeFs * 0.62 + padX * 2;
                                const approxH = badgeFs + padY * 2;
                                // translate para a aresta snappada, depois rotaciona em torno de (0,0)
                                const svgAngle = -(fq.rotation || 0);
                                return (
                                    <g key={`fq-${p.id}-${fi}`}
                                        transform={`translate(${fq.svgX}, ${fq.svgY}) rotate(${svgAngle})`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => { e.stopPropagation(); if (!dragRef.current?.moved && onFaceQuadraSelect) onFaceQuadraSelect(fq.value); }}>
                                        <rect
                                            x={-approxW / 2}
                                            y={-approxH / 2}
                                            width={approxW} height={approxH}
                                            rx={approxH * 0.3}
                                            fill="#E65100" fillOpacity={0.92}
                                        />
                                        <text
                                            x={0} y={0}
                                            textAnchor="middle" dominantBaseline="middle"
                                            fontSize={badgeFs} fill="#fff" fontWeight={700}
                                            pointerEvents="none"
                                        >
                                            {fq.text}
                                        </text>
                                    </g>
                                );
                            })
                        )}
                    </g>
                </svg>
            </div>
            {/* Rodapé */}
            <div className="px-2 py-1 small text-muted" style={{ borderTop: '1px solid #dee2e6' }}>
                {polygons.length} lote(s) no arquivo
                {selectedId !== null && (() => {
                    const p = polygons.find(x => x.id === selectedId);
                    const edgeCount = selectedEdges?.filter(e => e.polyId === p.id).length ?? 0;
                    return p ? (
                        <span className="ms-3 text-primary fw-semibold">
                            ✓ {p.codigo ?? `Lote ${p.id + 1}`} selecionado
                            {edgeCount > 0
                                ? <span className="ms-2 text-warning fw-bold">· {edgeCount} aresta{edgeCount > 1 ? 's' : ''} (testada)</span>
                                : <span className="ms-2 text-muted fw-normal">· Clique em aresta(s) para definir a testada</span>
                            }
                        </span>
                    ) : null;
                })()}
            </div>
        </div>
    );
}

// ── Componente principal ─────────────────────────────────────────────────────
function LoteCalculator() {
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES,
    });

    const mapRef = useRef(null);
    const activePolygonRef = useRef(null);

    // ── Modo de entrada de área ────────────────────────────────────────────
    const [inputMode, setInputMode] = useState(null); // null | 'google' | 'manual' | 'dxf'

    // ── Busca de endereço (Google mode) ───────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [cepQuery, setCepQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    // ── Área / coordenadas ─────────────────────────────────────────────────
    const [area, setArea] = useState(null);
    const [coordinates, setCoordinates] = useState(null);

    // ── Manual mode ────────────────────────────────────────────────────────
    const [manualArea, setManualArea] = useState('');
    const [manualTestada, setManualTestada] = useState('');
    const [manualProf, setManualProf] = useState('');

    // ── DXF mode ───────────────────────────────────────────────────────────
    const [dxfPolygons, setDxfPolygons] = useState(() => _dxfCache.polygons);
    const [dxfSelectedId, setDxfSelectedId] = useState(() => _dxfCache.selectedId);
    const [dxfSelectedEdges, setDxfSelectedEdges] = useState(() => _dxfCache.selectedEdges);
    const [dxfUnit, setDxfUnit] = useState(() => _dxfCache.unit);
    const [dxfError, setDxfError] = useState('');
    const [dxfFileName, setDxfFileName] = useState(() => _dxfCache.fileName);
    const [dxfSearchQuery, setDxfSearchQuery] = useState('');
    const [dxfSearchError, setDxfSearchError] = useState('');
    const [dxfFocusId, setDxfFocusId] = useState(null);
    const [dxfAutoCode, setDxfAutoCode] = useState(null);

    // Sincroniza cache DXF (persiste entre navegações de rota)
    useEffect(() => {
        _dxfCache.polygons = dxfPolygons;
        _dxfCache.fileName = dxfFileName;
        _dxfCache.unit = dxfUnit;
        _dxfCache.selectedId = dxfSelectedId;
        _dxfCache.selectedEdges = dxfSelectedEdges;
    }, [dxfPolygons, dxfFileName, dxfUnit, dxfSelectedId, dxfSelectedEdges]);

    // Ao montar: restaura área/testada do lote que estava selecionado no cache
    useEffect(() => {
        if (_dxfCache.selectedId !== null && _dxfCache.polygons.length > 0) {
            const unitMult = { m: 1, cm: 0.0001, mm: 0.000001 };
            const poly = _dxfCache.polygons.find(p => p.id === _dxfCache.selectedId);
            if (poly) {
                setArea(poly.area * (unitMult[_dxfCache.unit] || 1));
                if (_dxfCache.selectedEdges.length > 0) {
                    const totalM = _dxfCache.selectedEdges.reduce((sum, e) => sum + e.rawLength * (unitMult[_dxfCache.unit] || 1), 0);
                    if (totalM > 0) setTestada(totalM.toFixed(2));
                }
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Imóvel ─────────────────────────────────────────────────────────────
    const [imovelData, setImovelData] = useState(null);

    // ── Fatores de homogeneização ──────────────────────────────────────────
    const [fatorOptions, setFatorOptions] = useState(FATORES_LOTE_DEFAULT);
    const [ftopo, setFtopo] = useState(1.0);
    const [fforma, setFforma] = useState(1.0);
    const [finfra, setFinfra] = useState(1.0);
    const [ffre, setFfre] = useState(1.0);
    const [testada, setTestada] = useState('');
    const [profundidade, setProfundidade] = useState('');
    const [areaPadrao, setAreaPadrao] = useState('');
    const [encravado, setEncravado] = useState(false);

    const fprof = React.useMemo(() => {
        if (!testada || !profundidade || parseFloat(profundidade) === 0 || !fatorOptions) return 1.0;
        const fp = parseFloat(testada) / parseFloat(profundidade);
        const tabela = [...(fatorOptions.fprof || [])].sort((a, b) => a.limite_sup - b.limite_sup);
        return (tabela.find(r => fp <= r.limite_sup) || tabela[tabela.length - 1])?.fator ?? 1.0;
    }, [testada, profundidade, fatorOptions]);

    const farea = React.useMemo(() => {
        if (!areaPadrao || parseFloat(areaPadrao) === 0 || !area || !fatorOptions) return 1.0;
        const ratio = area / parseFloat(areaPadrao);
        const tabela = [...(fatorOptions.farea || [])].sort((a, b) => a.limite_sup - b.limite_sup);
        const match = tabela.find(r => ratio <= r.limite_sup) || tabela[tabela.length - 1];
        if (!match) return 1.0;
        if (match.formula === 'power_neg04') return Math.pow(ratio, -0.4);
        return match.fator ?? 1.0;
    }, [area, areaPadrao, fatorOptions]);

    useEffect(() => {
        axios.get(`${API_URL}/api/parametros`, { withCredentials: true })
            .then(res => setFatorOptions(res.data.fatores_lote))
            .catch(() => {});
    }, []);

    // ── Profundidade equivalente automática: Área / Testada ────────────────
    const autoProfRef = React.useRef(null);
    useEffect(() => {
        if (!area || !testada || parseFloat(testada) <= 0) return;
        const computed = (area / parseFloat(testada)).toFixed(2);
        if (!profundidade || profundidade === autoProfRef.current) {
            autoProfRef.current = computed;
            setProfundidade(computed);
        }
    }, [area, testada]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Formulário ─────────────────────────────────────────────────────────
    const [nomeLote, setNomeLote] = useState('');
    const [tipo, setTipo] = useState('lote');
    const [endereco, setEndereco] = useState('');
    const [valorPorM2, setValorPorM2] = useState('');
    const [deducaoPercentual, setDeducaoPercentual] = useState('0');

    // ── Feedback ───────────────────────────────────────────────────────────
    const [resultado, setResultado] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // ── Histórico ──────────────────────────────────────────────────────────
    const [historico, setHistorico] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loteToDelete, setLoteToDelete] = useState(null);

    // ── Persistência de estado via sessionStorage ─────────────────────────
    useEffect(() => {
        const saved = sessionStorage.getItem('lote_form_state');
        if (saved) {
            try {
                const s = JSON.parse(saved);
                if (s.nomeLote !== undefined) setNomeLote(s.nomeLote);
                if (s.tipo) setTipo(s.tipo);
                if (s.endereco !== undefined) setEndereco(s.endereco);
                if (s.valorPorM2 !== undefined) setValorPorM2(s.valorPorM2);
                if (s.deducaoPercentual !== undefined) setDeducaoPercentual(s.deducaoPercentual);
                if (s.imovelData) setImovelData(s.imovelData);
                if (s.testada !== undefined) setTestada(s.testada);
                if (s.profundidade !== undefined) setProfundidade(s.profundidade);
                if (s.areaPadrao !== undefined) setAreaPadrao(s.areaPadrao);
                if (s.ftopo !== undefined) setFtopo(s.ftopo);
                if (s.fforma !== undefined) setFforma(s.fforma);
                if (s.finfra !== undefined) setFinfra(s.finfra);
                if (s.ffre !== undefined) setFfre(s.ffre);
                if (s.encravado !== undefined) setEncravado(s.encravado);
                if (s.inputMode) setInputMode(s.inputMode);
                if (s.manualArea !== undefined) setManualArea(s.manualArea);
                if (s.manualTestada !== undefined) setManualTestada(s.manualTestada);
                if (s.manualProf !== undefined) setManualProf(s.manualProf);
            } catch {}
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        sessionStorage.setItem('lote_form_state', JSON.stringify({
            nomeLote, tipo, endereco, valorPorM2, deducaoPercentual, imovelData,
            testada, profundidade, areaPadrao, ftopo, fforma, finfra, ffre, encravado,
            inputMode, manualArea, manualTestada, manualProf,
        }));
    }, [nomeLote, tipo, endereco, valorPorM2, deducaoPercentual, imovelData,
        testada, profundidade, areaPadrao, ftopo, fforma, finfra, ffre, encravado,
        inputMode, manualArea, manualTestada, manualProf]);

    // ── Handlers Google Maps ───────────────────────────────────────────────
    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

    const handleSearchCep = useCallback(async () => {
        const cep = cepQuery.replace(/\D/g, '');
        if (cep.length !== 8) { setSearchError('CEP deve ter 8 dígitos.'); return; }
        setSearching(true); setSearchError(''); setSearchResults([]);
        try {
            const viacep = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await viacep.json();
            if (data.erro) { setSearchError('CEP não encontrado.'); return; }
            const enderecoBusca = `${data.logradouro || ''}, ${data.bairro || ''}, ${data.localidade}/${data.uf}, Brasil`;
            const nom = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(enderecoBusca)}&format=json&limit=5&countrycodes=br`,
                { headers: { 'Accept-Language': 'pt-BR', 'User-Agent': 'BenfeCalc/1.0' } }
            );
            const results = await nom.json();
            if (results.length === 0) {
                setSearchResults([{ display_name: enderecoBusca, lat: null, lon: null }]);
            } else {
                setSearchResults(results.map(r => ({ ...r, cepData: data })));
            }
        } catch { setSearchError('Erro na busca. Verifique sua conexão.'); }
        finally { setSearching(false); }
    }, [cepQuery]);

    const handleSearchAddress = useCallback(async () => {
        if (!searchQuery.trim()) return;
        setSearching(true); setSearchError(''); setSearchResults([]);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery + ', Brasil')}&format=json&limit=5&countrycodes=br`,
                { headers: { 'Accept-Language': 'pt-BR', 'User-Agent': 'BenfeCalc/1.0' } }
            );
            const results = await res.json();
            if (results.length === 0) setSearchError('Nenhum endereço encontrado.');
            else setSearchResults(results);
        } catch { setSearchError('Erro na busca.'); }
        finally { setSearching(false); }
    }, [searchQuery]);

    const handleSelectResult = useCallback((result) => {
        if (result.lat && result.lon && mapRef.current) {
            mapRef.current.panTo({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
            mapRef.current.setZoom(18);
        }
        setEndereco(result.display_name || '');
        setSearchResults([]); setSearchQuery(''); setCepQuery('');
    }, []);

    const computeAreaAndCoords = useCallback((polygon) => {
        const areaM2 = window.google.maps.geometry.spherical.computeArea(polygon.getPath());
        setArea(areaM2);
        setResultado(null);
        const coords = polygon.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
        setCoordinates(JSON.stringify(coords));
    }, []);

    const onPolygonComplete = useCallback((polygon) => {
        if (activePolygonRef.current) activePolygonRef.current.setMap(null);
        activePolygonRef.current = polygon;
        computeAreaAndCoords(polygon);
        const path = polygon.getPath();
        path.addListener('set_at', () => computeAreaAndCoords(polygon));
        path.addListener('insert_at', () => computeAreaAndCoords(polygon));
        path.addListener('remove_at', () => computeAreaAndCoords(polygon));
    }, [computeAreaAndCoords]);

    const handleClearPolygon = () => {
        if (activePolygonRef.current) { activePolygonRef.current.setMap(null); activePolygonRef.current = null; }
        setArea(null); setCoordinates(null); setResultado(null);
    };

    // ── Handler Manual ─────────────────────────────────────────────────────
    const handleManualConfirm = () => {
        const val = parseFloat(manualArea);
        if (!val || val <= 0) { setError('Informe uma área válida.'); return; }
        setArea(val);
        setCoordinates(null);
        setResultado(null);
        // preenche testada/prof se informados
        if (manualTestada) setTestada(manualTestada);
        if (manualProf) setProfundidade(manualProf);
        setError('');
    };

    // ── Handlers DXF ──────────────────────────────────────────────────────
    const handleDxfFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setDxfError(''); setDxfPolygons([]); setDxfSelectedId(null); setArea(null); setCoordinates(null);
        setDxfFileName(file.name);
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const parser = new DxfParser();
                const dxf = parser.parseSync(ev.target.result);
                const polys = extractPolygons(dxf);
                if (polys.length === 0) {
                    setDxfError('Nenhum polígono fechado encontrado. Verifique se o DXF contém polilíneas fechadas (LWPOLYLINE/POLYLINE com flag closed).');
                } else {
                    setDxfPolygons(polys);
                }
            } catch (err) {
                setDxfError('Erro ao ler o arquivo DXF: ' + err.message);
            }
        };
        reader.readAsText(file);
    };

    const unitMultiplier = { m: 1, cm: 0.0001, mm: 0.000001 };

    const handleDxfSelect = (polygon) => {
        setDxfSelectedId(polygon.id);
        setDxfSelectedEdges([]); // limpa arestas ao trocar de lote
        setTestada('');
        const areaM2 = polygon.area * unitMultiplier[dxfUnit];
        setArea(areaM2);
        setCoordinates(JSON.stringify(polygon.pts));
        setResultado(null);
        if (polygon.valorGenerico != null) {
            setValorPorM2(String(polygon.valorGenerico));
        }
        if (polygon.codigo) {
            setDxfAutoCode(polygon.codigo);
        }
    };

    const handleDxfEdgeSelect = (polyId, edgeIdx, rawLength) => {
        setDxfSelectedEdges(prev => {
            const exists = prev.some(e => e.polyId === polyId && e.edgeIdx === edgeIdx);
            const newEdges = exists
                ? prev.filter(e => !(e.polyId === polyId && e.edgeIdx === edgeIdx))
                : [...prev, { polyId, edgeIdx, rawLength }];
            const totalM = newEdges.reduce((sum, e) => sum + e.rawLength * unitMultiplier[dxfUnit], 0);
            setTestada(totalM > 0 ? totalM.toFixed(2) : '');
            return newEdges;
        });
    };

    const handleDxfSearch = () => {
        const q = dxfSearchQuery.trim().toLowerCase();
        if (!q) return;
        const found = dxfPolygons.find(p =>
            (p.codigo ?? String(p.id + 1)).toLowerCase().includes(q)
        );
        if (found) {
            setDxfSearchError('');
            handleDxfSelect(found);
            setDxfFocusId(prev => prev === found.id ? prev + 0.001 : found.id); // força re-trigger
        } else {
            setDxfSearchError(`Lote "${dxfSearchQuery}" não encontrado.`);
        }
    };

    // ── Troca de modo — mantém DXF ativo entre modos ─────────────────────
    const handleModeChange = (mode) => {
        setInputMode(prev => prev === mode ? null : mode);
        setArea(null); setCoordinates(null); setResultado(null);
        if (activePolygonRef.current) { activePolygonRef.current.setMap(null); activePolygonRef.current = null; }
    };

    // ── Remove DXF explicitamente (para substituição) ─────────────────────
    const handleDxfRemove = () => {
        setDxfPolygons([]); setDxfSelectedId(null); setDxfSelectedEdges([]);
        setDxfError(''); setDxfFileName(''); setDxfSearchQuery(''); setDxfSearchError('');
        setDxfFocusId(null); setArea(null); setCoordinates(null);
    };

    // ── Calcular / Salvar ──────────────────────────────────────────────────
    const handleCalculate = async () => {
        setError(''); setSuccess('');
        if (!area) { setError('Defina a área do lote primeiro.'); return; }
        if (!valorPorM2 || parseFloat(valorPorM2) <= 0) { setError('Informe o valor por m².'); return; }
        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/lote/calculate`, {
                area_total_m2: area,
                valor_por_m2: parseFloat(valorPorM2),
                deducao_percentual: parseFloat(deducaoPercentual || 0),
                ftopo, fforma, finfra, ffre, fprof, farea, encravado,
            }, { withCredentials: true });
            setResultado(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao calcular.');
        } finally { setLoading(false); }
    };

    const handleSave = async () => {
        if (!resultado) return;
        setLoading(true); setError('');
        try {
            await axios.post(`${API_URL}/api/lote/calculations`, {
                nome_lote: nomeLote, tipo, endereco, coordinates,
                area_total_m2: area,
                valor_por_m2: parseFloat(valorPorM2),
                deducao_percentual: parseFloat(deducaoPercentual || 0),
                imovel_codigo: imovelData?.codigo || dxfAutoCode || '',
                imovel_nome: imovelData?.nome || '',
                ftopo, fforma, finfra, ffre, fprof, farea, encravado,
            }, { withCredentials: true });
            setSuccess('Cálculo de lote salvo com sucesso!');
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao salvar.');
        } finally { setLoading(false); }
    };

    const loadHistorico = async () => {
        setLoadingHistory(true); setError('');
        try {
            const response = await axios.get(`${API_URL}/api/lote/calculations`, { withCredentials: true });
            setHistorico(response.data); setShowHistory(true);
        } catch { setError('Erro ao carregar histórico.'); }
        finally { setLoadingHistory(false); }
    };

    const confirmDelete = async () => {
        if (!loteToDelete) return;
        try {
            await axios.delete(`${API_URL}/api/lote/calculations/${loteToDelete.id}`, { withCredentials: true });
            setHistorico(prev => prev.filter(l => l.id !== loteToDelete.id));
            setShowDeleteModal(false); setLoteToDelete(null);
        } catch { setError('Erro ao deletar.'); }
    };

    // ── Botão de modo ──────────────────────────────────────────────────────
    const ModeButton = ({ mode, label, icon }) => {
        const active = inputMode === mode;
        return (
            <button
                type="button"
                onClick={() => handleModeChange(mode)}
                style={{
                    flex: 1,
                    padding: '14px 8px',
                    border: active ? '2px solid #0d6efd' : '2px solid #dee2e6',
                    borderRadius: 10,
                    background: active ? '#e8f0fe' : '#fff',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s',
                    fontWeight: active ? 600 : 400,
                    color: active ? '#0d6efd' : '#495057',
                }}
            >
                <div style={{ fontSize: 26, marginBottom: 4 }}>{icon}</div>
                <div style={{ fontSize: 13 }}>{label}</div>
                {active && <div style={{ fontSize: 11, color: '#0d6efd', marginTop: 2 }}>selecionado ▲</div>}
            </button>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <Card className="p-4 mx-auto" style={{ maxWidth: '1100px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Avaliação de Lote / Loteamento</h2>
                <Button variant="outline-secondary" onClick={loadHistorico} disabled={loadingHistory}>
                    {loadingHistory ? <Spinner animation="border" size="sm" /> : 'Histórico de Lotes'}
                </Button>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            <ImovelSelector
                key={dxfAutoCode || 'manual'}
                onSelect={setImovelData}
                initialCodigo={dxfAutoCode}
            />

            {/* ── Seletor de modo ─────────────────────────────────────── */}
            <Card className="mb-4">
                <Card.Body>
                    <h5 className="mb-3">1. Como deseja definir a área do lote?</h5>
                    <div className="d-flex gap-3" style={{ flexWrap: 'wrap' }}>
                        <ModeButton mode="google" label="Google Maps" icon="🗺️" />
                        <ModeButton mode="manual" label="Inserção Manual" icon="✏️" />
                        <ModeButton mode="dxf"    label="Importar DXF"  icon="📐" />
                    </div>
                </Card.Body>
            </Card>

            {/* ── Modo: Google Maps ───────────────────────────────────── */}
            {inputMode === 'google' && (
                <Card className="mb-4 border-primary">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0 text-primary">Google Maps — Desenhar polígono</h5>
                            {area && (
                                <Button variant="outline-danger" size="sm" onClick={handleClearPolygon}>
                                    Limpar polígono
                                </Button>
                            )}
                        </div>
                        <p className="text-muted small mb-2">
                            Use a ferramenta de polígono para delimitar o lote. O polígono é editável após ser desenhado.
                        </p>

                        {!GOOGLE_MAPS_API_KEY ? (
                            <Alert variant="warning">
                                Chave da API do Google Maps não configurada.
                                Defina <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> no ambiente.
                            </Alert>
                        ) : loadError ? (
                            <Alert variant="danger">Erro ao carregar Google Maps: {loadError.message}</Alert>
                        ) : !isLoaded ? (
                            <div className="text-center p-5"><Spinner animation="border" /><p className="mt-2">Carregando mapa...</p></div>
                        ) : (
                            <>
                                <Row className="g-2 mb-2">
                                    <Col md={4}>
                                        <div className="input-group input-group-sm">
                                            <Form.Control
                                                type="text"
                                                placeholder="CEP (somente números)"
                                                value={cepQuery}
                                                onChange={e => setCepQuery(e.target.value.replace(/\D/g, '').slice(0, 8))}
                                                onKeyDown={e => e.key === 'Enter' && handleSearchCep()}
                                                maxLength={8}
                                            />
                                            <Button variant="outline-secondary" size="sm" onClick={handleSearchCep} disabled={searching}>
                                                {searching ? <Spinner animation="border" size="sm" /> : 'Buscar CEP'}
                                            </Button>
                                        </div>
                                    </Col>
                                    <Col md={8}>
                                        <div className="input-group input-group-sm">
                                            <Form.Control
                                                type="text"
                                                placeholder="Ou busque por endereço livre: Rua, Bairro, Cidade/UF"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleSearchAddress()}
                                            />
                                            <Button variant="outline-primary" size="sm" onClick={handleSearchAddress} disabled={searching}>
                                                {searching ? <Spinner animation="border" size="sm" /> : 'Buscar'}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                                {searchError && <Alert variant="warning" className="py-1 px-2 mb-2" style={{ fontSize: '0.82rem' }}>{searchError}</Alert>}
                                {searchResults.length > 0 && (
                                    <div className="mb-2 border rounded" style={{ maxHeight: 180, overflowY: 'auto', background: '#fff', zIndex: 10, position: 'relative' }}>
                                        {searchResults.map((r, i) => (
                                            <div key={i} className="px-3 py-2 border-bottom"
                                                style={{ cursor: 'pointer', fontSize: '0.82rem' }}
                                                onClick={() => handleSelectResult(r)}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                            >
                                                {r.display_name}
                                                {!r.lat && <span className="text-muted ms-2">(sem coordenadas — endereço preenchido)</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <GoogleMap
                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                    center={DEFAULT_CENTER}
                                    zoom={14}
                                    onLoad={onMapLoad}
                                    options={{ mapTypeId: 'hybrid', mapTypeControl: true, streetViewControl: false, fullscreenControl: true }}
                                >
                                    <DrawingManager
                                        onPolygonComplete={onPolygonComplete}
                                        options={{
                                            drawingControl: true,
                                            drawingControlOptions: {
                                                position: window.google.maps.ControlPosition.TOP_CENTER,
                                                drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
                                            },
                                            polygonOptions: {
                                                fillColor: '#1976D2', fillOpacity: 0.25,
                                                strokeColor: '#1976D2', strokeWeight: 2,
                                                editable: true, draggable: true,
                                            },
                                        }}
                                    />
                                </GoogleMap>
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* ── Modo: Manual ────────────────────────────────────────── */}
            {inputMode === 'manual' && (
                <Card className="mb-4 border-secondary">
                    <Card.Body>
                        <h5 className="mb-3 text-secondary">Inserção Manual das Dimensões</h5>
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label><strong>Área total (m²)</strong></Form.Label>
                                    <Form.Control
                                        type="number" step="0.01" min="0.01"
                                        placeholder="Ex: 360.00"
                                        value={manualArea}
                                        onChange={e => setManualArea(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Testada (m) <small className="text-muted">opcional</small></Form.Label>
                                    <Form.Control
                                        type="number" step="0.01" min="0"
                                        placeholder="Ex: 12.00"
                                        value={manualTestada}
                                        onChange={e => setManualTestada(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Profundidade (m) <small className="text-muted">opcional</small></Form.Label>
                                    <Form.Control
                                        type="number" step="0.01" min="0"
                                        placeholder="Ex: 30.00"
                                        value={manualProf}
                                        onChange={e => setManualProf(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Button variant="secondary" className="w-100" onClick={handleManualConfirm}>
                                    Confirmar
                                </Button>
                            </Col>
                        </Row>
                        <p className="text-muted small mt-2 mb-0">
                            Testada e profundidade são opcionais — usados apenas para calcular automaticamente o fator Fprof.
                        </p>
                    </Card.Body>
                </Card>
            )}

            {/* ── Modo: DXF ───────────────────────────────────────────── */}
            {inputMode === 'dxf' && (
                <Card className="mb-4 border-info">
                    <Card.Body>
                        <h5 className="mb-1 text-info">Importar arquivo DXF</h5>
                        <p className="text-muted small mb-3">
                            Importe um arquivo <strong>.dxf</strong> com os lotes desenhados como polilíneas fechadas
                            (LWPOLYLINE/POLYLINE com flag <em>closed</em>). Clique no lote desejado para obter a área.
                        </p>
                        {dxfError && <Alert variant="warning" className="py-2">{dxfError}</Alert>}
                        <Row className="g-3 align-items-end mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label><strong>Arquivo DXF</strong></Form.Label>
                                    {dxfFileName ? (
                                        <div className="d-flex align-items-center gap-2 border rounded px-3 py-2 bg-white">
                                            <span className="text-muted small flex-grow-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                📄 {dxfFileName}
                                            </span>
                                            <Button variant="outline-danger" size="sm" onClick={handleDxfRemove}>
                                                Remover
                                            </Button>
                                        </div>
                                    ) : (
                                        <Form.Control
                                            type="file"
                                            accept=".dxf"
                                            onChange={handleDxfFile}
                                        />
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Unidade do desenho</Form.Label>
                                    <Form.Select value={dxfUnit} onChange={e => {
                                        setDxfUnit(e.target.value);
                                        // recalculate area if a polygon is already selected
                                        if (dxfSelectedId !== null) {
                                            const poly = dxfPolygons.find(p => p.id === dxfSelectedId);
                                            if (poly) setArea(poly.area * unitMultiplier[e.target.value]);
                                        }
                                    }}>
                                        <option value="m">Metros (m)</option>
                                        <option value="cm">Centímetros (cm)</option>
                                        <option value="mm">Milímetros (mm)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        {dxfPolygons.length > 0 && (
                            <Row className="g-2 mb-3 align-items-end">
                                <Col>
                                    <Form.Control
                                        type="text"
                                        placeholder="Pesquisar lote por código (ex: 7898)"
                                        value={dxfSearchQuery}
                                        onChange={e => { setDxfSearchQuery(e.target.value); setDxfSearchError(''); }}
                                        onKeyDown={e => e.key === 'Enter' && handleDxfSearch()}
                                    />
                                    {dxfSearchError && <Form.Text className="text-danger">{dxfSearchError}</Form.Text>}
                                </Col>
                                <Col xs="auto">
                                    <Button variant="outline-primary" onClick={handleDxfSearch}>Localizar</Button>
                                </Col>
                            </Row>
                        )}
                        <DxfViewer
                            polygons={dxfPolygons}
                            selectedId={dxfSelectedId}
                            onSelect={handleDxfSelect}
                            unitMultiplier={unitMultiplier}
                            dxfUnit={dxfUnit}
                            selectedEdges={dxfSelectedEdges}
                            onSelectEdge={handleDxfEdgeSelect}
                            focusId={dxfFocusId}
                            onFaceQuadraSelect={(value) => setValorPorM2(String(value))}
                        />
                    </Card.Body>
                </Card>
            )}

            {/* ── Área selecionada (comum a todos os modos) ────────────── */}
            {area && (
                <div className="mb-4 p-3 bg-light border rounded d-flex align-items-center gap-3 flex-wrap">
                    <div>
                        <strong>Área selecionada:</strong>{' '}
                        <Badge bg="primary" style={{ fontSize: '1rem' }}>
                            {area.toFixed(2)} m²
                        </Badge>
                    </div>
                    <div className="text-muted">= {(area / 10000).toFixed(4)} ha</div>
                    {tipo === 'loteamento' && parseFloat(deducaoPercentual) > 0 && (
                        <div>
                            <strong>Área útil:</strong>{' '}
                            <Badge bg="success" style={{ fontSize: '1rem' }}>
                                {(area * (1 - parseFloat(deducaoPercentual) / 100)).toFixed(2)} m²
                            </Badge>
                            <span className="text-muted ms-1">(após {deducaoPercentual}% de dedução)</span>
                        </div>
                    )}
                    <Button variant="outline-danger" size="sm" onClick={() => { setArea(null); setCoordinates(null); setResultado(null); }}>
                        Limpar
                    </Button>
                </div>
            )}

            {/* ── Passo 1b — Fatores ──────────────────────────────────── */}
            <Card className="mb-4">
                <Card.Body>
                    <h5 className="mb-1">1b. Características do Lote <small className="text-muted fw-normal fs-6">(Fatores de Homogeneização)</small></h5>
                    <p className="text-muted small mb-3">
                        Preencha as características para calcular o valor homogeneizado. Fatores padrão = 1,00 (neutro).
                    </p>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Topografia (Ftopo)</Form.Label>
                                <Form.Select value={ftopo} onChange={e => setFtopo(parseFloat(e.target.value))}>
                                    {fatorOptions.ftopo.map((o, i) => <option key={i} value={o.fator}>{o.descricao}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Forma (Fforma)</Form.Label>
                                <Form.Select value={fforma} onChange={e => setFforma(parseFloat(e.target.value))}>
                                    {fatorOptions.fforma.map((o, i) => <option key={i} value={o.fator}>{o.descricao}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Infraestrutura (Finfra)</Form.Label>
                                <Form.Select value={finfra} onChange={e => setFinfra(parseFloat(e.target.value))}>
                                    {fatorOptions.finfra.map((o, i) => <option key={i} value={o.fator}>{o.descricao}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Frequência/Uso (Ffre)</Form.Label>
                                <Form.Select value={ffre} onChange={e => setFfre(parseFloat(e.target.value))}>
                                    {fatorOptions.ffre.map((o, i) => <option key={i} value={o.fator}>{o.descricao}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Testada (m) <small className="text-muted">— para calcular Fprof</small></Form.Label>
                                <Form.Control
                                    type="number" step="0.01" min="0"
                                    placeholder="Ex: 12.0"
                                    value={testada}
                                    onChange={e => setTestada(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Profundidade (m) <small className="text-muted">— para calcular Fprof</small>
                                    {profundidade && profundidade === autoProfRef.current && (
                                        <small className="ms-1 text-success">(calculado: Área ÷ Testada)</small>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    type="number" step="0.01" min="0"
                                    placeholder="Ex: 30.0"
                                    value={profundidade}
                                    onChange={e => { autoProfRef.current = null; setProfundidade(e.target.value); }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Área Padrão (m²) <small className="text-muted">— para calcular Farea</small></Form.Label>
                                <Form.Control
                                    type="number" step="0.01" min="0"
                                    placeholder="Ex: 360.0"
                                    value={areaPadrao}
                                    onChange={e => setAreaPadrao(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="d-flex align-items-center gap-4 flex-wrap mb-2">
                        <Form.Check
                            type="checkbox"
                            label="Lote encravado (sem frente para via pública)"
                            checked={encravado}
                            onChange={e => setEncravado(e.target.checked)}
                        />
                    </div>
                    <div className="d-flex gap-3 flex-wrap mt-2 p-2 bg-light rounded small">
                        <span><strong>Ftopo:</strong> {ftopo.toFixed(3)}</span>
                        <span><strong>Fforma:</strong> {fforma.toFixed(3)}</span>
                        <span><strong>Finfra:</strong> {finfra.toFixed(3)}</span>
                        <span><strong>Ffre:</strong> {ffre.toFixed(3)}</span>
                        <span><strong>Fprof:</strong> {fprof.toFixed(3)}{testada && profundidade ? ` (T/P=${(parseFloat(testada)/parseFloat(profundidade)).toFixed(3)})` : ' (auto)'}</span>
                        <span><strong>Farea:</strong> {farea.toFixed(4)}{area && areaPadrao ? (() => { const r = area/parseFloat(areaPadrao); return r > 10 ? ` (GLEBA: (${r.toFixed(2)})^-0,4)` : ` (A/Ap=${r.toFixed(3)})`; })() : ' (auto)'}</span>
                        {encravado && <span className="text-danger"><strong>Fencravado:</strong> {fatorOptions?.encravado_fator?.toFixed(3) ?? '0.750'}</span>}
                        <span className="fw-bold text-primary">
                            Fh (produto) ≈ {(ftopo * fforma * finfra * ffre * fprof * farea * (encravado ? (fatorOptions?.encravado_fator ?? 0.75) : 1.0)).toFixed(4)}
                        </span>
                    </div>
                </Card.Body>
            </Card>

            {/* ── Passo 2 — Informações ───────────────────────────────── */}
            <Card className="mb-4">
                <Card.Body>
                    <h5 className="mb-3">2. Informações do Lote</h5>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nome / Identificação</Form.Label>
                                <Form.Control
                                    type="text" placeholder="Ex: Lote 12 Quadra 5"
                                    value={nomeLote} onChange={e => setNomeLote(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Tipo</Form.Label>
                                <Form.Select value={tipo} onChange={e => { setTipo(e.target.value); setResultado(null); }}>
                                    <option value="lote">Lote único</option>
                                    <option value="loteamento">Loteamento (área total com infraestrutura)</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Endereço</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Preenchido automaticamente pela busca ou edite manualmente"
                                    value={endereco} onChange={e => setEndereco(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={tipo === 'loteamento' ? 6 : 12}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <strong>Valor por m² (R$)</strong>
                                    {tipo === 'loteamento' && <small className="text-muted d-block">Valor dos lotes comercializáveis</small>}
                                </Form.Label>
                                <Form.Control
                                    type="number" placeholder="Ex: 350.00" step="0.01" min="0.01"
                                    value={valorPorM2} onChange={e => { setValorPorM2(e.target.value); setResultado(null); }}
                                />
                            </Form.Group>
                        </Col>
                        {tipo === 'loteamento' && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <strong>Dedução para infraestrutura (%)</strong>
                                        <small className="text-muted d-block">Vias, praças, áreas verdes (típico: 30–40%)</small>
                                    </Form.Label>
                                    <Form.Control
                                        type="number" placeholder="Ex: 35" step="0.1" min="0" max="99"
                                        value={deducaoPercentual} onChange={e => { setDeducaoPercentual(e.target.value); setResultado(null); }}
                                    />
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                    <Button variant="primary" onClick={handleCalculate} disabled={loading || !area}>
                        {loading ? <><Spinner animation="border" size="sm" className="me-2" />Calculando...</> : 'Calcular Valor'}
                    </Button>
                </Card.Body>
            </Card>

            {/* ── Passo 3 — Resultado ─────────────────────────────────── */}
            {resultado && (
                <Card className="mb-4 border-success">
                    <Card.Header className="bg-success text-white">
                        <h5 className="mb-0">3. Resultado da Avaliação</h5>
                    </Card.Header>
                    <Card.Body>
                        <Row className="text-center g-3 mb-3">
                            <Col md={3}>
                                <div className="p-3 bg-light rounded h-100">
                                    <div className="text-muted small mb-1">Área Total</div>
                                    <div className="fs-5 fw-bold">{resultado.area_total_m2.toLocaleString('pt-BR')} m²</div>
                                    <div className="text-muted small">{(resultado.area_total_m2 / 10000).toFixed(4)} ha</div>
                                </div>
                            </Col>
                            {resultado.deducao_percentual > 0 && (
                                <>
                                    <Col md={3}>
                                        <div className="p-3 bg-light rounded h-100">
                                            <div className="text-muted small mb-1">Dedução Infra ({resultado.deducao_percentual}%)</div>
                                            <div className="fs-5 fw-bold text-warning">
                                                -{(resultado.area_total_m2 - resultado.area_util_m2).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m²
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="p-3 bg-light rounded h-100">
                                            <div className="text-muted small mb-1">Área Útil (comercializável)</div>
                                            <div className="fs-5 fw-bold">{resultado.area_util_m2.toLocaleString('pt-BR')} m²</div>
                                        </div>
                                    </Col>
                                </>
                            )}
                            <Col md={resultado.deducao_percentual > 0 ? 3 : 6}>
                                <div className="p-3 bg-light rounded h-100">
                                    <div className="text-muted small mb-1">Valor Unitário (R$/m²)</div>
                                    <div className="fs-5 fw-bold">R$ {resultado.valor_por_m2?.toFixed(2)}</div>
                                    {resultado.valor_homogeneizado && (
                                        <div className="text-primary small mt-1">
                                            Homog.: R$ {resultado.valor_homogeneizado.toFixed(2)}/m²
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="p-3 bg-success bg-opacity-10 border border-success rounded h-100">
                                    <div className="text-muted small mb-1">
                                        {resultado.valor_homogeneizado ? 'Valor Total Homogeneizado' : 'Valor Total Estimado'}
                                    </div>
                                    <div className="fs-2 fw-bold text-success">
                                        R$ {resultado.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-muted small">@ R$ {resultado.valor_por_m2?.toFixed(2)}/m²</div>
                                </div>
                            </Col>
                        </Row>
                        {resultado.fatores && (
                            <div className="p-3 bg-light rounded mb-3 small">
                                <strong className="d-block mb-2">Fatores de Homogeneização aplicados:</strong>
                                <div className="d-flex gap-3 flex-wrap">
                                    {Object.entries(resultado.fatores).map(([k, v]) => (
                                        <span key={k} className={v !== 1.0 ? 'text-primary fw-bold' : 'text-muted'}>
                                            <strong>{k}:</strong> {typeof v === 'number' ? v.toFixed(4) : v}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <Button variant="success" onClick={handleSave} disabled={loading}>
                            {loading ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : 'Salvar Cálculo'}
                        </Button>
                    </Card.Body>
                </Card>
            )}

            {/* ── Modal Histórico ─────────────────────────────────────── */}
            <Modal show={showHistory} onHide={() => setShowHistory(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Histórico de Avaliações de Lotes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historico.length === 0 ? (
                        <Alert variant="info">Nenhum cálculo de lote salvo ainda.</Alert>
                    ) : (
                        <Table striped bordered hover responsive>
                            <thead className="table-dark">
                                <tr>
                                    <th>ID</th><th>Nome</th><th>Tipo</th><th>Endereço</th>
                                    <th>Área Total (m²)</th><th>Área Útil (m²)</th>
                                    <th>R$/m²</th><th>Valor Total (R$)</th><th>Data</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {historico.map(l => (
                                    <tr key={l.id}>
                                        <td><Badge bg="primary">{l.id}</Badge></td>
                                        <td><strong>{l.nome_lote || 'Sem nome'}</strong></td>
                                        <td><Badge bg={l.tipo === 'loteamento' ? 'info' : 'secondary'}>{l.tipo}</Badge></td>
                                        <td><small>{l.endereco || '-'}</small></td>
                                        <td>{l.area_total_m2?.toLocaleString('pt-BR')}</td>
                                        <td>{l.area_util_m2?.toLocaleString('pt-BR')}</td>
                                        <td>R$ {l.valor_por_m2?.toFixed(2)}</td>
                                        <td><strong>R$ {l.valor_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></td>
                                        <td><small>{l.timestamp ? new Date(l.timestamp).toLocaleString('pt-BR') : '-'}</small></td>
                                        <td>
                                            <Button variant="outline-danger" size="sm"
                                                onClick={() => { setLoteToDelete(l); setShowDeleteModal(true); }}>
                                                🗑️
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>

            {/* ── Modal Confirmar Exclusão ─────────────────────────────── */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Exclusão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Deletar o cálculo <strong>"{loteToDelete?.nome_lote || 'Sem nome'}"</strong>?</p>
                    <small className="text-danger">Esta ação não pode ser desfeita.</small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
                    <Button variant="danger" onClick={confirmDelete}>Deletar</Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}

export default LoteCalculator;
